/** @jsx jsx */

/* eslint-disable camelcase */
import { css, jsx } from '@emotion/react';

import type Maybe from 'graphql/tsutils/Maybe';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

// Save/Unsave Experiment
import SavingBookmark from 'bundles/browse-common/components/bookmark/SavingBookmark';
import { SAVING_BOOKMARK_BLACK } from 'bundles/browse-common/components/bookmark/utils/BookmarkUtils';
import type { SavedProductStringTypes } from 'bundles/browse-common/types/SavedProductQueries';
import type { withSaveDataForSearchCards } from 'bundles/browse-common/types/withSavedDataFromProducts';
import { getSavingProductType } from 'bundles/browse-common/utils/productUtils';
import { logo } from 'bundles/coursera-plus-common/utils/courseraPlusUtils';
import { useCourseraPlusProductOwnerships } from 'bundles/coursera-plus-common/utils/withCourseraPlusProductOwnerships';
import ProductDifficultyLabel from 'bundles/search-common/components/ProductDifficultyLabel';
import Ratings from 'bundles/search-common/components/Ratings';
import { GUIDED_PROJECT, RHYME_PROJECT, SELF_PACED_PROJECT } from 'bundles/search-common/constants/entityTypes';
import { getCleanIdFromObjectId } from 'bundles/search-common/utils/getCleanIdFromObjectId';

import _t from 'i18n!nls/search-common';

export const VIEWS_AND_ENROLLMENT_THRESHOLD = 1500;

const styles = {
  container: css`
    padding: 0;

    .rating-enroll-wrapper {
      display: flex;
      align-items: center;
      padding-top: 12px;

      .rc-Ratings {
        height: 100%;
        align-items: center;
        margin: 0;
      }

      .ratings-text,
      .enrollment-number,
      .ratings-count {
        font-family: var(--cds-font-family-source-sans-pro) !important;
        color: var(--cds-color-neutral-primary);
      }

      .ratings-text {
        font-family: var(--cds-font-family-source-sans-pro);
        padding-right: 0;
      }

      .ratings-count {
        font-family: var(--cds-font-family-source-sans-pro);
        white-space: pre;

        &::before {
          content: ' ';
          display: inline-block;
        }
      }

      .enrollment {
        span {
          font-family: var(--cds-font-family-source-sans-pro);
          color: var(--cds-color-neutral-primary);
        }

        .enrollment-number {
          font-family: var(--cds-font-family-source-sans-pro);
          padding-right: 0;
        }
      }

      .info-item {
        display: flex;
        height: 20px;
        align-items: center;
        border-left: 1px solid var(--cds-color-neutral-primary);
        padding-left: 8px;
        padding-right: 8px;

        &:first-of-type {
          border-left: none;
          padding-left: 0;
        }

        &:last-of-type {
          padding-right: 0;
        }
      }
    }

    .tagline {
      color: var(--cds-color-grey-700);
      margin: 0;
    }

    .product-difficulty {
      padding-top: 10px;
    }
  `,
};

const Formatters = {
  largeNumber(val: number, capitalizeAbbreviation = false) {
    const orderOfMagnitude = Math.floor(Math.log(val) / Math.log(10));
    if (orderOfMagnitude < 3) {
      return String(Math.round(val));
    } else if (orderOfMagnitude < 6) {
      // unclear if the suggested ** operator will be properly pollyfilled for IE
      // eslint-disable-next-line no-restricted-properties
      const numberOfThousands = (val / Math.pow(10, 3)).toLocaleString(undefined, {
        maximumSignificantDigits: 2,
      });
      // the eslint error is going off because this isn't a direct child of a function but since it is the return value of a function called in the request context it should still translate correctly
      return capitalizeAbbreviation
        ? _t('#{numberOfThousands}K', { numberOfThousands })
        : _t('#{numberOfThousands}k', { numberOfThousands });
    } else {
      // eslint-disable-next-line no-restricted-properties
      const numberOfMillions = (val / Math.pow(10, 6)).toLocaleString(undefined, {
        maximumSignificantDigits: 2,
      });
      return capitalizeAbbreviation
        ? _t('#{numberOfMillions}M', { numberOfMillions })
        : _t('#{numberOfMillions}m', { numberOfMillions });
    }
    // TODO: ADD BILLIONS?? :D
  },
};

type ProductInfoProps = withSaveDataForSearchCards & {
  avgProductRating?: Maybe<number>;
  numProductRatings?: Maybe<number>;
  productDifficultyLevel?: Maybe<string | false>;
  enrollments?: Maybe<number>;
  tagline?: Maybe<string>;
  isPartOfCourseraPlus?: boolean;
  entityType?: string;
  id?: string;
  isSaved?: boolean;
  loadingSavedProducts?: boolean;
  showCourseraLitePill?: boolean;
};

const ProductInfo = ({
  avgProductRating,
  productDifficultyLevel,
  enrollments,
  tagline,
  numProductRatings,
  isPartOfCourseraPlus,
  entityType,
  id,
  isSaved,
  loadingSavedProducts,
  showCourseraLitePill,
}: ProductInfoProps) => {
  const { ownsCourseraPlus } = useCourseraPlusProductOwnerships();
  const resolvedID = getCleanIdFromObjectId(id || '') || '';
  const showEnrollments = enrollments && enrollments > VIEWS_AND_ENROLLMENT_THRESHOLD;

  const isEntityTypeExcludedFromCourseraLite =
    entityType && [GUIDED_PROJECT, RHYME_PROJECT, SELF_PACED_PROJECT].includes(entityType);
  const showCourseraLite = !ownsCourseraPlus && showCourseraLitePill && !isEntityTypeExcludedFromCourseraLite;

  return (
    <div className="rc-ProductInfo" css={styles.container}>
      <div className="rating-enroll-wrapper">
        {!!avgProductRating && !!numProductRatings && numProductRatings > 5 && (
          <div className="info-item">
            <Ratings ratingCount={numProductRatings} averageFiveStarRating={Math.round(avgProductRating * 10) / 10} />
          </div>
        )}
        {!!showEnrollments && (
          <div className="enrollment info-item">
            <FormattedMessage
              message={_t('{numberOfStudents} students')}
              numberOfStudents={<span className="enrollment-number">{Formatters.largeNumber(enrollments)}</span>}
            />
          </div>
        )}
        {ownsCourseraPlus && isPartOfCourseraPlus && (
          <div className="info-item">
            <Imgix src={logo.PLUS_PILL_BLUE} alt={_t('Coursera Plus')} maxWidth={37} maxHeight={16} />
          </div>
        )}
        {showCourseraLite && isPartOfCourseraPlus && (
          <div className="info-item">
            <Imgix src={logo.LITE_PILL_BLUE} alt={_t('Coursera Plus')} maxWidth={37} maxHeight={16} />
          </div>
        )}
      </div>
      {tagline && <p className="tagline">{tagline}</p>}
      {productDifficultyLevel && <ProductDifficultyLabel productDifficultyLevel={productDifficultyLevel} />}
      {id && entityType && !loadingSavedProducts && (
        <SavingBookmark
          saved={isSaved}
          product={{
            product_id: resolvedID,
            product_type: getSavingProductType(entityType) as SavedProductStringTypes,
          }}
          color={SAVING_BOOKMARK_BLACK}
          shouldShowText={true}
          shouldUseSmallSize={true}
        />
      )}
    </div>
  );
};

export default ProductInfo;
